import React, { useState } from 'react';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AccountsTable from './components/AccountsTable';
import ClaimsPanel from './components/ClaimsPanel';
import RevenueAnalytics from './components/RevenueAnalytics';
import PaymentProcessor from './components/PaymentProcessor';
import InsuranceVerification from './components/InsuranceVerification';

const BillingManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('accounts');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showPaymentProcessor, setShowPaymentProcessor] = useState(false);

  const mockAccounts = [
    {
      id: 1,
      patientName: "Sarah Johnson",
      accountNumber: "ACC-2026-001",
      dob: "03/15/1985",
      balance: 2450.00,
      insuranceBalance: 1850.00,
      insuranceProvider: "Blue Cross Blue Shield",
      insuranceId: "BCBS-789456123",
      status: "overdue",
      lastPayment: "12/10/2025",
      daysOutstanding: 45
    },
    {
      id: 2,
      patientName: "Michael Chen",
      accountNumber: "ACC-2026-002",
      dob: "07/22/1978",
      balance: 850.00,
      insuranceBalance: 0.00,
      insuranceProvider: "Aetna",
      insuranceId: "AET-456789012",
      status: "pending",
      lastPayment: "01/05/2026",
      daysOutstanding: 9
    },
    {
      id: 3,
      patientName: "Emily Rodriguez",
      accountNumber: "ACC-2026-003",
      dob: "11/08/1992",
      balance: 0.00,
      insuranceBalance: 0.00,
      insuranceProvider: "UnitedHealthcare",
      insuranceId: "UHC-123456789",
      status: "paid",
      lastPayment: "01/12/2026",
      daysOutstanding: 0
    },
    {
      id: 4,
      patientName: "David Thompson",
      accountNumber: "ACC-2026-004",
      dob: "05/30/1965",
      balance: 3200.00,
      insuranceBalance: 2800.00,
      insuranceProvider: "Medicare",
      insuranceId: "MED-987654321",
      status: "denied",
      lastPayment: "11/20/2025",
      daysOutstanding: 55
    },
    {
      id: 5,
      patientName: "Jennifer Martinez",
      accountNumber: "ACC-2026-005",
      dob: "09/14/1988",
      balance: 1200.00,
      insuranceBalance: 900.00,
      insuranceProvider: "Cigna",
      insuranceId: "CIG-654321098",
      status: "pending",
      lastPayment: "01/08/2026",
      daysOutstanding: 6
    }
  ];

  const mockClaims = [
    {
      id: 1,
      claimNumber: "CLM-2026-001",
      patientName: "Sarah Johnson",
      serviceDate: "12/15/2025",
      claimAmount: 1850.00,
      insuranceProvider: "Blue Cross Blue Shield",
      cptCode: "99214",
      icdCodes: ["E11.9", "I10"],
      diagnoses: [
        { code: "E11.9", description: "Type 2 diabetes mellitus without complications" },
        { code: "I10", description: "Essential (primary) hypertension" }
      ],
      status: "denied",
      denialReason: "Prior authorization required for this service. Please submit authorization documentation.",
      submittedDate: "12/20/2025"
    },
    {
      id: 2,
      claimNumber: "CLM-2026-002",
      patientName: "Michael Chen",
      serviceDate: "01/03/2026",
      claimAmount: 650.00,
      insuranceProvider: "Aetna",
      cptCode: "99213",
      icdCodes: ["J45.909"],
      diagnoses: [
        { code: "J45.909", description: "Unspecified asthma, uncomplicated" }
      ],
      status: "submitted",
      submittedDate: "01/05/2026"
    },
    {
      id: 3,
      claimNumber: "CLM-2026-003",
      patientName: "Emily Rodriguez",
      serviceDate: "01/10/2026",
      claimAmount: 450.00,
      insuranceProvider: "UnitedHealthcare",
      cptCode: "99212",
      icdCodes: ["R51"],
      diagnoses: [
        { code: "R51", description: "Headache" }
      ],
      status: "approved",
      submittedDate: "01/11/2026",
      approvedDate: "01/12/2026"
    },
    {
      id: 4,
      claimNumber: "CLM-2026-004",
      patientName: "David Thompson",
      serviceDate: "11/28/2025",
      claimAmount: 2800.00,
      insuranceProvider: "Medicare",
      cptCode: "99215",
      icdCodes: ["E11.22", "N18.3"],
      diagnoses: [
        { code: "E11.22", description: "Type 2 diabetes mellitus with diabetic chronic kidney disease" },
        { code: "N18.3", description: "Chronic kidney disease, stage 3 (moderate)" }
      ],
      status: "appealed",
      denialReason: "Medical necessity not established. Additional documentation required.",
      submittedDate: "12/01/2025",
      appealDate: "12/15/2025"
    },
    {
      id: 5,
      claimNumber: "CLM-2026-005",
      patientName: "Jennifer Martinez",
      serviceDate: "01/06/2026",
      claimAmount: 900.00,
      insuranceProvider: "Cigna",
      cptCode: "99214",
      icdCodes: ["F41.1"],
      diagnoses: [
        { code: "F41.1", description: "Generalized anxiety disorder" }
      ],
      status: "pending",
      submittedDate: "01/08/2026"
    }
  ];

  const mockMetrics = [
    {
      id: 1,
      type: 'collection',
      label: 'Collection Rate',
      value: '94.2%',
      subtitle: 'Last 30 days',
      trend: 2.5,
      isPositiveGood: true,
      benchmark: '92%',
      percentage: 94
    },
    {
      id: 2,
      type: 'ar',
      label: 'Days in AR',
      value: '38',
      subtitle: 'Average days',
      trend: -5.2,
      isPositiveGood: false,
      benchmark: '45 days',
      percentage: 65
    },
    {
      id: 3,
      type: 'denial',
      label: 'Denial Rate',
      value: '6.8%',
      subtitle: 'Current month',
      trend: -1.2,
      isPositiveGood: false,
      benchmark: '8%',
      percentage: 85
    },
    {
      id: 4,
      type: 'revenue',
      label: 'Monthly Revenue',
      value: '$284,500',
      subtitle: 'January 2026',
      trend: 8.3,
      isPositiveGood: true,
      benchmark: '$250K',
      percentage: 88
    }
  ];

  const mockVerifications = [
    {
      id: 1,
      patientName: "Sarah Johnson",
      insuranceProvider: "Blue Cross Blue Shield",
      policyNumber: "BCBS789456123",
      groupNumber: "GRP-12345",
      effectiveDate: "01/01/2026",
      status: "verified",
      coverageStatus: "active",
      deductible: 1500,
      copay: 25,
      notes: "Coverage verified for outpatient services. Prior authorization required for procedures over $1000."
    },
    {
      id: 2,
      patientName: "Michael Chen",
      insuranceProvider: "Aetna",
      policyNumber: "AET456789012",
      groupNumber: "GRP-67890",
      effectiveDate: "01/01/2026",
      status: "pending",
      coverageStatus: "active",
      deductible: 2000,
      copay: 30,
      notes: "Verification in progress. Expected completion within 24 hours."
    },
    {
      id: 3,
      patientName: "David Thompson",
      insuranceProvider: "Medicare",
      policyNumber: "MED987654321",
      groupNumber: "N/A",
      effectiveDate: "06/01/2023",
      status: "expired",
      coverageStatus: "inactive",
      deductible: 0,
      copay: 0,
      notes: "Coverage expired. Patient needs to update insurance information."
    },
    {
      id: 4,
      patientName: "Jennifer Martinez",
      insuranceProvider: "Cigna",
      policyNumber: "CIG654321098",
      groupNumber: "GRP-54321",
      effectiveDate: "01/01/2026",
      status: "verified",
      coverageStatus: "limited",
      deductible: 1000,
      copay: 20,
      notes: "Limited coverage for specialty services. Pre-authorization required for referrals."
    }
  ];

  const currentPatient = {
    name: "Sarah Johnson",
    mrn: "MRN-2026-001",
    dob: "03/15/1985",
    age: 40
  };

  const patientAlerts = [
    {
      id: 1,
      severity: 'critical',
      title: 'Outstanding Balance',
      message: 'Account overdue by 45 days. Total balance: $2,450.00',
      timestamp: '2 hours ago',
      actionRequired: 'Contact patient for payment arrangement',
      dismissible: false
    },
    {
      id: 2,
      severity: 'warning',
      title: 'Claim Denied',
      message: 'Recent claim denied due to missing prior authorization',
      timestamp: '1 day ago',
      actionRequired: 'Submit appeal with authorization documentation',
      dismissible: true
    }
  ];

  const quickActions = [
    {
      id: 'process-payment',
      label: 'Process Payment',
      icon: 'DollarSign',
      onClick: () => {
        if (mockAccounts?.[0]) {
          setSelectedAccount(mockAccounts?.[0]);
          setShowPaymentProcessor(true);
        }
      },
      shortcut: 'Ctrl+P'
    },
    {
      id: 'submit-claim',
      label: 'Submit Claim',
      icon: 'FileText',
      onClick: () => console.log('Submit claim clicked'),
      shortcut: 'Ctrl+C'
    },
    {
      id: 'verify-insurance',
      label: 'Verify Insurance',
      icon: 'Shield',
      onClick: () => setActiveView('verification'),
      shortcut: 'Ctrl+V'
    },
    {
      id: 'generate-statement',
      label: 'Generate Statement',
      icon: 'Receipt',
      onClick: () => console.log('Generate statement clicked'),
      shortcut: 'Ctrl+S'
    }
  ];

  const handleAccountClick = (account) => {
    setSelectedAccount(account);
    console.log('Account clicked:', account);
  };

  const handlePaymentClick = (account) => {
    setSelectedAccount(account);
    setShowPaymentProcessor(true);
  };

  const handleProcessPayment = (paymentData) => {
    console.log('Payment processed:', paymentData);
  };

  const handleSubmitClaim = () => {
    console.log('Submit new claim');
  };

  const handleAppealClaim = (claim) => {
    console.log('Appeal claim:', claim);
  };

  const handleVerifyInsurance = (verification) => {
    console.log('Verify insurance:', verification);
  };

  const handleViewVerificationDetails = (verification) => {
    console.log('View verification details:', verification);
  };

  const handleAlertClick = (alert) => {
    console.log('Alert clicked:', alert);
  };

  const views = [
    { id: 'accounts', label: 'Accounts', icon: 'Users' },
    { id: 'claims', label: 'Claims', icon: 'FileText' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'verification', label: 'Verification', icon: 'Shield' }
  ];

  return (
    <div className="min-h-screen bg-background pt-16">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className="">
        <PatientContextBar
          patient={currentPatient}
          alerts={patientAlerts}
          onAlertClick={handleAlertClick}
        />

        <main className="p-4 md:p-6 lg:p-8">
          <div className="max-w-screen-2xl mx-auto">
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">Billing Management</h1>
                  <p className="text-sm md:text-base text-muted-foreground">Comprehensive revenue cycle management and financial operations</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                  >
                    <span className="hidden sm:inline">Export Report</span>
                    <span className="sm:hidden">Export</span>
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                  >
                    <span className="hidden sm:inline">New Transaction</span>
                    <span className="sm:hidden">New</span>
                  </Button>
                </div>
              </div>

              <div className="flex gap-2 overflow-x-auto pb-2">
                {views?.map((view) => (
                  <button
                    key={view?.id}
                    onClick={() => setActiveView(view?.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth whitespace-nowrap flex-shrink-0 ${
                      activeView === view?.id
                        ? 'bg-primary text-primary-foreground shadow-elevation-2'
                        : 'bg-card text-muted-foreground hover:bg-muted border border-border'
                    }`}
                  >
                    <Icon name={view?.icon} size={18} />
                    {view?.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              {activeView === 'accounts' && (
                <AccountsTable
                  accounts={mockAccounts}
                  onAccountClick={handleAccountClick}
                  onPaymentClick={handlePaymentClick}
                />
              )}

              {activeView === 'claims' && (
                <ClaimsPanel
                  claims={mockClaims}
                  onSubmitClaim={handleSubmitClaim}
                  onAppealClaim={handleAppealClaim}
                />
              )}

              {activeView === 'analytics' && (
                <RevenueAnalytics metrics={mockMetrics} />
              )}

              {activeView === 'verification' && (
                <InsuranceVerification
                  verifications={mockVerifications}
                  onVerify={handleVerifyInsurance}
                  onViewDetails={handleViewVerificationDetails}
                />
              )}
            </div>
          </div>
        </main>
      </div>
      {showPaymentProcessor && selectedAccount && (
        <PaymentProcessor
          account={selectedAccount}
          onClose={() => {
            setShowPaymentProcessor(false);
            setSelectedAccount(null);
          }}
          onProcessPayment={handleProcessPayment}
        />
      )}
      <QuickActions actions={quickActions} position="bottom-right" />
    </div>
  );
};

export default BillingManagement;